import React, { useState } from "react";
import PropTypes from "prop-types";
import {
  formatUtcForLocalDisplay,
  parseLocalTimeFromUtc,
  combineUtcDateWithLocalTime,
  validateArrivalTime,
} from "utils/date";

const ArrivalTimeDisplay = ({
  arrivalTime,
  overrideArrivalTime,
  onUpdateArrivalTime,
  disabled = false,
}) => {
  const [isEditing, setIsEditing] = useState(false);
  const [showConfirmation, setShowConfirmation] = useState(false);

  // State for time components only (no date)
  const [editHour, setEditHour] = useState("");
  const [editMinute, setEditMinute] = useState("");

  // Touch feedback state
  const [touchFeedback, setTouchFeedback] = useState("");

  // Get the display time (override takes precedence)
  const displayTime = overrideArrivalTime || arrivalTime;

  // Format UTC timestamp for display in local timezone using French locale
  const formatTimestamp = (utcTimestamp) => {
    return formatUtcForLocalDisplay(utcTimestamp);
  };

  // Parse UTC timestamp to extract local time components
  const parseTimeFromTimestamp = (utcTimestamp) => {
    return parseLocalTimeFromUtc(utcTimestamp);
  };

  // Combine original UTC date with new local time components
  const combineOriginalDateWithNewTime = (
    originalUtcTimestamp,
    newLocalHour,
    newLocalMinute
  ) => {
    return combineUtcDateWithLocalTime(
      originalUtcTimestamp,
      newLocalHour,
      newLocalMinute
    );
  };

  // Generate hour options (00-23)
  const hourOptions = Array.from({ length: 24 }, (_, i) =>
    String(i).padStart(2, "0")
  );

  // Generate minute options (00, 05, 10, 15, ..., 55)
  const minuteOptions = Array.from({ length: 12 }, (_, i) =>
    String(i * 5).padStart(2, "0")
  );

  // Touch feedback helper
  const triggerTouchFeedback = (action) => {
    setTouchFeedback(action);
    // Vibrate if supported
    if (navigator.vibrate) {
      navigator.vibrate(50);
    }
    setTimeout(() => setTouchFeedback(""), 150);
  };

  const handleEditClick = () => {
    triggerTouchFeedback("edit");
    const parsed = parseTimeFromTimestamp(displayTime);
    setEditHour(String(parsed.hour).padStart(2, "0"));
    setEditMinute(String(parsed.minute).padStart(2, "0"));
    setIsEditing(true);
  };

  const handleSaveClick = () => {
    if (!editHour || !editMinute) return;

    triggerTouchFeedback("save");

    // Create the new UTC timestamp with local time input
    const newUtcTimestamp = combineOriginalDateWithNewTime(
      displayTime,
      parseInt(editHour),
      parseInt(editMinute)
    );

    // Validate the time using timezone-aware validation
    const validation = validateArrivalTime(newUtcTimestamp);
    if (!validation.isValid) {
      alert(validation.errorMessage);
      return;
    }

    setShowConfirmation(true);
  };

  const handleConfirmSave = () => {
    triggerTouchFeedback("confirm");
    const formattedTime = combineOriginalDateWithNewTime(
      displayTime,
      parseInt(editHour),
      parseInt(editMinute)
    );
    onUpdateArrivalTime(formattedTime);
    setIsEditing(false);
    setShowConfirmation(false);
    setEditHour("");
    setEditMinute("");
  };

  const handleCancelEdit = () => {
    triggerTouchFeedback("cancel");
    setIsEditing(false);
    setShowConfirmation(false);
    setEditHour("");
    setEditMinute("");
  };

  // Format time for digital display
  const formatDigitalTime = (hour, minute) => {
    const hourStr = hour || "00";
    const minuteStr = minute || "00";
    return `${hourStr}:${minuteStr}`;
  };

  // Don't render if no arrival time exists
  if (!displayTime) {
    return null;
  }

  return (
    <div className="arrival-time-display">
      <div className="arrival-time-display__content">
        <div className="arrival-time-display__label">
          <strong>Heure d&apos;arrivée</strong>
        </div>

        {!isEditing ? (
          <div className="arrival-time-display__time-section">
            <div
              className={`arrival-time-display__time ${
                overrideArrivalTime
                  ? "arrival-time-display__time--overridden"
                  : ""
              }`}
            >
              {formatTimestamp(displayTime)}
              {overrideArrivalTime && (
                <span className="arrival-time-display__override-indicator">
                  (modifiée)
                </span>
              )}
            </div>
            {!disabled && (
              <button
                type="button"
                className="arrival-time-display__edit-button"
                onClick={handleEditClick}
              >
                Modifier
              </button>
            )}
          </div>
        ) : (
          <div className="arrival-time-display__edit-section">
            <div className="arrival-time-display__edit-header">
              <h4>Modifier l&apos;heure d&apos;arrivée</h4>
              <p>Sélectionnez l&apos;heure et les minutes</p>
            </div>

            <div className="arrival-time-display__digital-time">
              <span className="arrival-time-display__digital-display">
                {formatDigitalTime(editHour, editMinute)}
              </span>
            </div>

            <div className="arrival-time-display__time-section-edit">
              <div className="arrival-time-display__time-inputs">
                <div className="arrival-time-display__hour-section">
                  <label className="arrival-time-display__input-label">
                    Heure
                  </label>
                  <select
                    value={editHour}
                    onChange={(e) => setEditHour(e.target.value)}
                    className={`arrival-time-display__time-select ${
                      touchFeedback === "hour"
                        ? "arrival-time-display__input--feedback"
                        : ""
                    }`}
                    onFocus={() => triggerTouchFeedback("hour")}
                  >
                    <option value="">--</option>
                    {hourOptions.map((hour) => (
                      <option key={hour} value={hour}>
                        {hour}h
                      </option>
                    ))}
                  </select>
                </div>

                <div className="arrival-time-display__minute-section">
                  <label className="arrival-time-display__input-label">
                    Minutes
                  </label>
                  <select
                    value={editMinute}
                    onChange={(e) => setEditMinute(e.target.value)}
                    className={`arrival-time-display__time-select ${
                      touchFeedback === "minute"
                        ? "arrival-time-display__input--feedback"
                        : ""
                    }`}
                    onFocus={() => triggerTouchFeedback("minute")}
                  >
                    <option value="">--</option>
                    {minuteOptions.map((minute) => (
                      <option key={minute} value={minute}>
                        {minute}min
                      </option>
                    ))}
                  </select>
                </div>
              </div>
            </div>

            <div className="arrival-time-display__edit-actions">
              <button
                type="button"
                className={`arrival-time-display__save-button ${
                  touchFeedback === "save"
                    ? "arrival-time-display__button--feedback"
                    : ""
                }`}
                onClick={handleSaveClick}
                disabled={!editHour || !editMinute}
              >
                <i className="fas fa-check"></i>
                Enregistrer
              </button>
              <button
                type="button"
                className={`arrival-time-display__cancel-button ${
                  touchFeedback === "cancel"
                    ? "arrival-time-display__button--feedback"
                    : ""
                }`}
                onClick={handleCancelEdit}
              >
                <i className="fas fa-times"></i>
                Annuler
              </button>
            </div>
          </div>
        )}
      </div>

      {showConfirmation && (
        <div className="arrival-time-display__confirmation-modal">
          <div className="arrival-time-display__confirmation-content">
            <h3>Confirmer la modification</h3>
            <p>
              Voulez-vous vraiment modifier l&apos;heure d&apos;arrivée à :
              <br />
              <strong>
                {formatTimestamp(
                  combineOriginalDateWithNewTime(
                    displayTime,
                    editHour,
                    editMinute
                  )
                )}
              </strong>
            </p>
            <div className="arrival-time-display__confirmation-actions">
              <button
                type="button"
                className="arrival-time-display__confirm-button"
                onClick={handleConfirmSave}
              >
                Confirmer
              </button>
              <button
                type="button"
                className="arrival-time-display__cancel-button"
                onClick={() => setShowConfirmation(false)}
              >
                Annuler
              </button>
            </div>
          </div>
        </div>
      )}
    </div>
  );
};

ArrivalTimeDisplay.propTypes = {
  arrivalTime: PropTypes.string,
  overrideArrivalTime: PropTypes.string,
  onUpdateArrivalTime: PropTypes.func.isRequired,
  disabled: PropTypes.bool,
};

export default ArrivalTimeDisplay;
